import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

/**
 * GymDataService
 *
 * Single shared source of truth for EVERY workout dropdown / ion-select in
 * XtraPush. The same data lives in src/assets/gym-data.json (loaded at runtime
 * via HttpClient so it can be edited without a rebuild) and as a typed const
 * SEED below (so getters always return data synchronously, before the HTTP
 * load resolves; Angular 5 templates bind synchronously).
 *
 * TypeScript 2.6 safe: plain interfaces + const arrays, no enums-as-namespaces,
 * no optional chaining, no nullish coalescing, no JSON module imports.
 */

export interface Measure {
  type: string;   // 'stack' | 'kg' | 'level'
  unit: string;   // 'placa' | 'kg' | 'nivel'
  min: number;
  max: number;
  step: number;
}

export interface MuscleGroup {
  id: string;
  name: string;
}

export interface EquipmentType {
  id: string;
  name: string;
  measure: Measure;
}

export interface Machine {
  id: string;
  qr: string;
  name: string;
  muscleGroup: string;   // -> MuscleGroup.id
  equipment: string;     // -> EquipmentType.id
  measure: Measure;      // machine's own measurement scale
}

export interface Exercise {
  id: string;
  name: string;
  muscleGroup: string;       // -> MuscleGroup.id
  defaultMachine?: string;   // -> Machine.id
  equipment?: string;        // -> EquipmentType.id
  cues?: string[];           // feeds the per-exercise cue checklist
}

export interface SetScheme {
  id: string;
  name: string;
}

export interface RepRange {
  id: string;
  name: string;
}

export interface WeightUnit {
  id: string;
  name: string;
}

export interface RpeOption {
  value: number;
  label: string;
}

export interface RestOption {
  seconds: number;
  label: string;
}

export interface SetNumber {
  value: number;
  label: string;
}

export interface WorkoutTemplate {
  id: string;
  name: string;
  exerciseIds: string[];
}

export interface GymData {
  muscleGroups: MuscleGroup[];
  equipment: EquipmentType[];
  machines: Machine[];
  exercises: Exercise[];
  setSchemes: SetScheme[];
  repRanges: RepRange[];
  weightUnits: WeightUnit[];
  rpeScale: RpeOption[];
  restOptions: RestOption[];
  setNumbers: SetNumber[];
  workoutTemplates: WorkoutTemplate[];
}

/**
 * SEED: in-memory mirror of assets/gym-data.json. Used immediately so every
 * dropdown has data on first render; refreshed from the JSON file on load.
 */
const SEED: GymData = {
  muscleGroups: [
    { id: 'pecho', name: 'Pecho' },
    { id: 'espalda', name: 'Espalda' },
    { id: 'piernas', name: 'Piernas' },
    { id: 'hombros', name: 'Hombros' },
    { id: 'brazos', name: 'Brazos' },
    { id: 'core', name: 'Core' }
  ],
  equipment: [
    { id: 'maquina-guiada', name: 'Maquina guiada', measure: { type: 'stack', unit: 'placa', min: 1, max: 20, step: 1 } },
    { id: 'mancuernas', name: 'Peso libre (mancuernas)', measure: { type: 'kg', unit: 'kg', min: 1, max: 60, step: 1 } },
    { id: 'barra', name: 'Peso libre (barra)', measure: { type: 'kg', unit: 'kg', min: 20, max: 260, step: 2.5 } },
    { id: 'polea', name: 'Polea / cable', measure: { type: 'stack', unit: 'placa', min: 1, max: 20, step: 1 } },
    { id: 'maquina-placas', name: 'Maquina de placas', measure: { type: 'kg', unit: 'kg', min: 0, max: 300, step: 5 } },
    { id: 'peso-corporal', name: 'Peso corporal', measure: { type: 'level', unit: 'nivel', min: 1, max: 10, step: 1 } },
    { id: 'banda-elastica', name: 'Banda elastica', measure: { type: 'level', unit: 'nivel', min: 1, max: 6, step: 1 } },
    { id: 'cardio', name: 'Cardio', measure: { type: 'level', unit: 'nivel', min: 1, max: 20, step: 1 } }
  ],
  machines: [
    // ─── PECHO ────────────────────────────────────────────────────────────
    { id: 'press-banca', qr: 'XP-BANCA', name: 'Press de Banca Plano', muscleGroup: 'pecho', equipment: 'barra', measure: { type: 'kg', unit: 'kg', min: 20, max: 180, step: 2.5 } },
    { id: 'press-banca-inclinado', qr: 'XP-BANCA-INC', name: 'Press de Banca Inclinado', muscleGroup: 'pecho', equipment: 'barra', measure: { type: 'kg', unit: 'kg', min: 20, max: 160, step: 2.5 } },
    { id: 'press-banca-declinado', qr: 'XP-BANCA-DEC', name: 'Press de Banca Declinado', muscleGroup: 'pecho', equipment: 'barra', measure: { type: 'kg', unit: 'kg', min: 20, max: 160, step: 2.5 } },
    { id: 'press-pecho', qr: 'XP-PECHO', name: 'Press de Pecho (Maquina)', muscleGroup: 'pecho', equipment: 'maquina-guiada', measure: { type: 'stack', unit: 'placa', min: 1, max: 16, step: 1 } },
    { id: 'press-pecho-inclinado-maq', qr: 'XP-PECHO-INC', name: 'Press Inclinado (Maquina)', muscleGroup: 'pecho', equipment: 'maquina-guiada', measure: { type: 'stack', unit: 'placa', min: 1, max: 16, step: 1 } },
    { id: 'press-mancuernas', qr: 'XP-MANC-PRESS', name: 'Press con Mancuernas', muscleGroup: 'pecho', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 4, max: 50, step: 2 } },
    { id: 'aperturas-mancuernas', qr: 'XP-MANC-FLY', name: 'Aperturas con Mancuernas', muscleGroup: 'pecho', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 4, max: 30, step: 1 } },
    { id: 'peck-deck', qr: 'XP-PECK', name: 'Peck Deck (Aperturas)', muscleGroup: 'pecho', equipment: 'maquina-guiada', measure: { type: 'stack', unit: 'placa', min: 1, max: 16, step: 1 } },
    { id: 'cruce-poleas-pecho', qr: 'XP-CRUCE', name: 'Cruce de Poleas', muscleGroup: 'pecho', equipment: 'polea', measure: { type: 'stack', unit: 'placa', min: 1, max: 14, step: 1 } },

    // ─── ESPALDA ──────────────────────────────────────────────────────────
    { id: 'jalon-pecho', qr: 'XP-JALON', name: 'Jalon al Pecho', muscleGroup: 'espalda', equipment: 'polea', measure: { type: 'stack', unit: 'placa', min: 1, max: 18, step: 1 } },
    { id: 'jalon-cerrado', qr: 'XP-JALON-CERR', name: 'Jalon Cerrado (Triangulo)', muscleGroup: 'espalda', equipment: 'polea', measure: { type: 'stack', unit: 'placa', min: 1, max: 18, step: 1 } },
    { id: 'remo-sentado', qr: 'XP-REMO', name: 'Remo Sentado (Polea)', muscleGroup: 'espalda', equipment: 'polea', measure: { type: 'stack', unit: 'placa', min: 1, max: 18, step: 1 } },
    { id: 'remo-barra-t', qr: 'XP-T-ROW', name: 'Remo Barra T', muscleGroup: 'espalda', equipment: 'maquina-placas', measure: { type: 'kg', unit: 'kg', min: 20, max: 160, step: 5 } },
    { id: 'remo-mancuerna', qr: 'XP-REMO-MANC', name: 'Remo con Mancuerna', muscleGroup: 'espalda', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 4, max: 50, step: 2 } },
    { id: 'remo-pendlay', qr: 'XP-PENDLAY', name: 'Remo con Barra (Pendlay)', muscleGroup: 'espalda', equipment: 'barra', measure: { type: 'kg', unit: 'kg', min: 20, max: 160, step: 2.5 } },
    { id: 'dominadas-asistidas', qr: 'XP-DOM', name: 'Dominadas Asistidas', muscleGroup: 'espalda', equipment: 'peso-corporal', measure: { type: 'level', unit: 'nivel', min: 1, max: 12, step: 1 } },
    { id: 'pullover-mancuerna', qr: 'XP-PULLOVER', name: 'Pullover con Mancuerna', muscleGroup: 'espalda', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 6, max: 40, step: 2 } },
    { id: 'face-pull', qr: 'XP-FACE', name: 'Face Pull', muscleGroup: 'espalda', equipment: 'polea', measure: { type: 'stack', unit: 'placa', min: 1, max: 12, step: 1 } },

    // ─── PIERNAS ──────────────────────────────────────────────────────────
    { id: 'sentadilla-barra', qr: 'XP-SENT', name: 'Sentadilla con Barra', muscleGroup: 'piernas', equipment: 'barra', measure: { type: 'kg', unit: 'kg', min: 20, max: 240, step: 5 } },
    { id: 'prensa-piernas', qr: 'XP-PRENSA', name: 'Prensa de Piernas', muscleGroup: 'piernas', equipment: 'maquina-placas', measure: { type: 'stack', unit: 'placa', min: 1, max: 20, step: 1 } },
    { id: 'sentadilla-hack', qr: 'XP-HACK', name: 'Sentadilla Hack', muscleGroup: 'piernas', equipment: 'maquina-placas', measure: { type: 'kg', unit: 'kg', min: 0, max: 220, step: 5 } },
    { id: 'ext-cuadriceps', qr: 'XP-CUAD', name: 'Extension de Cuadriceps', muscleGroup: 'piernas', equipment: 'maquina-guiada', measure: { type: 'stack', unit: 'placa', min: 1, max: 15, step: 1 } },
    { id: 'curl-femoral', qr: 'XP-FEMORAL', name: 'Curl Femoral Acostado', muscleGroup: 'piernas', equipment: 'maquina-guiada', measure: { type: 'stack', unit: 'placa', min: 1, max: 15, step: 1 } },
    { id: 'curl-femoral-sentado', qr: 'XP-FEMORAL-SIT', name: 'Curl Femoral Sentado', muscleGroup: 'piernas', equipment: 'maquina-guiada', measure: { type: 'stack', unit: 'placa', min: 1, max: 15, step: 1 } },
    { id: 'peso-muerto-rumano', qr: 'XP-RDL', name: 'Peso Muerto Rumano', muscleGroup: 'piernas', equipment: 'barra', measure: { type: 'kg', unit: 'kg', min: 20, max: 220, step: 2.5 } },
    { id: 'hip-thrust', qr: 'XP-HIP', name: 'Hip Thrust', muscleGroup: 'piernas', equipment: 'barra', measure: { type: 'kg', unit: 'kg', min: 20, max: 220, step: 5 } },
    { id: 'gemelos-parado', qr: 'XP-GEMELOS', name: 'Gemelos Parado', muscleGroup: 'piernas', equipment: 'maquina-placas', measure: { type: 'kg', unit: 'kg', min: 10, max: 180, step: 5 } },
    { id: 'aductora', qr: 'XP-ADUCT', name: 'Aductora (Adentro)', muscleGroup: 'piernas', equipment: 'maquina-guiada', measure: { type: 'stack', unit: 'placa', min: 1, max: 14, step: 1 } },
    { id: 'abductora', qr: 'XP-ABDUCT', name: 'Abductora (Afuera)', muscleGroup: 'piernas', equipment: 'maquina-guiada', measure: { type: 'stack', unit: 'placa', min: 1, max: 14, step: 1 } },
    { id: 'zancadas-mancuernas', qr: 'XP-ZANCADA', name: 'Zancadas con Mancuernas', muscleGroup: 'piernas', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 4, max: 40, step: 2 } },

    // ─── HOMBROS ──────────────────────────────────────────────────────────
    { id: 'press-hombros', qr: 'XP-HOMBROS', name: 'Press de Hombros (Mancuernas)', muscleGroup: 'hombros', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 0, max: 50, step: 2 } },
    { id: 'press-hombros-maquina', qr: 'XP-HOMBROS-MAQ', name: 'Press de Hombros (Maquina)', muscleGroup: 'hombros', equipment: 'maquina-guiada', measure: { type: 'stack', unit: 'placa', min: 1, max: 16, step: 1 } },
    { id: 'press-militar-barra', qr: 'XP-MILITAR', name: 'Press Militar con Barra', muscleGroup: 'hombros', equipment: 'barra', measure: { type: 'kg', unit: 'kg', min: 20, max: 110, step: 2.5 } },
    { id: 'press-arnold', qr: 'XP-ARNOLD', name: 'Press Arnold', muscleGroup: 'hombros', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 4, max: 36, step: 2 } },
    { id: 'elevacion-lateral', qr: 'XP-LATERAL', name: 'Elevacion Lateral', muscleGroup: 'hombros', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 1, max: 30, step: 1 } },
    { id: 'elevacion-frontal', qr: 'XP-FRONTAL', name: 'Elevacion Frontal', muscleGroup: 'hombros', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 1, max: 30, step: 1 } },
    { id: 'pajaros', qr: 'XP-PAJAROS', name: 'Pajaros (Posteriores)', muscleGroup: 'hombros', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 1, max: 24, step: 1 } },
    { id: 'encogimientos', qr: 'XP-SHRUG', name: 'Encogimientos (Trapecios)', muscleGroup: 'hombros', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 8, max: 60, step: 2 } },

    // ─── BRAZOS ───────────────────────────────────────────────────────────
    { id: 'curl-biceps', qr: 'XP-BICEPS', name: 'Curl de Biceps (Mancuernas)', muscleGroup: 'brazos', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 1, max: 30, step: 1 } },
    { id: 'curl-biceps-barra', qr: 'XP-BICEPS-BARRA', name: 'Curl de Biceps con Barra', muscleGroup: 'brazos', equipment: 'barra', measure: { type: 'kg', unit: 'kg', min: 10, max: 60, step: 2.5 } },
    { id: 'curl-martillo', qr: 'XP-MARTILLO', name: 'Curl Martillo', muscleGroup: 'brazos', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 4, max: 32, step: 2 } },
    { id: 'curl-predicador', qr: 'XP-PREDIC', name: 'Curl Predicador', muscleGroup: 'brazos', equipment: 'maquina-guiada', measure: { type: 'stack', unit: 'placa', min: 1, max: 12, step: 1 } },
    { id: 'ext-triceps', qr: 'XP-TRICEPS', name: 'Extension de Triceps (Polea)', muscleGroup: 'brazos', equipment: 'polea', measure: { type: 'stack', unit: 'placa', min: 1, max: 16, step: 1 } },
    { id: 'press-frances', qr: 'XP-FRANCES', name: 'Press Frances', muscleGroup: 'brazos', equipment: 'barra', measure: { type: 'kg', unit: 'kg', min: 8, max: 60, step: 2.5 } },
    { id: 'ext-triceps-mancuerna', qr: 'XP-TRI-MANC', name: 'Extension Triceps (Mancuerna)', muscleGroup: 'brazos', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 4, max: 30, step: 1 } },
    { id: 'fondos-paralelas', qr: 'XP-FONDOS', name: 'Fondos en Paralelas', muscleGroup: 'brazos', equipment: 'peso-corporal', measure: { type: 'level', unit: 'nivel', min: 1, max: 10, step: 1 } },

    // ─── CORE ─────────────────────────────────────────────────────────────
    { id: 'abdominal-maquina', qr: 'XP-ABS', name: 'Abdominales en Maquina', muscleGroup: 'core', equipment: 'maquina-guiada', measure: { type: 'stack', unit: 'placa', min: 1, max: 15, step: 1 } },
    { id: 'crunch-polea', qr: 'XP-CRUNCH-POLEA', name: 'Crunch en Polea', muscleGroup: 'core', equipment: 'polea', measure: { type: 'stack', unit: 'placa', min: 1, max: 14, step: 1 } },
    { id: 'elevacion-piernas-colgado', qr: 'XP-PIERNAS-COLG', name: 'Elevacion de Piernas Colgado', muscleGroup: 'core', equipment: 'peso-corporal', measure: { type: 'level', unit: 'nivel', min: 1, max: 8, step: 1 } },
    { id: 'plancha', qr: 'XP-PLANCHA', name: 'Plancha', muscleGroup: 'core', equipment: 'peso-corporal', measure: { type: 'level', unit: 'nivel', min: 1, max: 8, step: 1 } },
    { id: 'rotacion-rusa', qr: 'XP-RUSO', name: 'Rotacion Rusa (Russian Twist)', muscleGroup: 'core', equipment: 'mancuernas', measure: { type: 'kg', unit: 'kg', min: 2, max: 24, step: 2 } },

    // ─── CARDIO ───────────────────────────────────────────────────────────
    { id: 'cinta', qr: 'XP-CINTA', name: 'Cinta de Correr', muscleGroup: 'piernas', equipment: 'cardio', measure: { type: 'level', unit: 'nivel', min: 1, max: 20, step: 1 } },
    { id: 'bicicleta-estatica', qr: 'XP-BICI', name: 'Bicicleta Estatica', muscleGroup: 'piernas', equipment: 'cardio', measure: { type: 'level', unit: 'nivel', min: 1, max: 20, step: 1 } },
    { id: 'eliptica', qr: 'XP-ELIP', name: 'Eliptica', muscleGroup: 'piernas', equipment: 'cardio', measure: { type: 'level', unit: 'nivel', min: 1, max: 20, step: 1 } },
    { id: 'remo-erg', qr: 'XP-ERG', name: 'Remo Ergometro', muscleGroup: 'espalda', equipment: 'cardio', measure: { type: 'level', unit: 'nivel', min: 1, max: 10, step: 1 } }
  ],
  exercises: [
    { id: 'press-banca-barra', name: 'Press de banca con barra', muscleGroup: 'pecho', defaultMachine: 'press-banca', equipment: 'barra', cues: ['Escapulas retraidas', 'Bajar a la linea del pecho', 'Rango completo', 'Sin rebote en el pecho'] },
    { id: 'press-pecho-maquina', name: 'Press de pecho en maquina', muscleGroup: 'pecho', defaultMachine: 'press-pecho', equipment: 'maquina-guiada', cues: ['Espalda apoyada', 'Codos a 45 grados', 'Rango completo', 'Sin impulso'] },
    { id: 'aperturas-peck', name: 'Aperturas en peck deck', muscleGroup: 'pecho', defaultMachine: 'peck-deck', equipment: 'maquina-guiada', cues: ['Pecho arriba', 'Movimiento controlado', 'Apreta al final'] },
    { id: 'press-inclinado-mancuernas', name: 'Press inclinado con mancuernas', muscleGroup: 'pecho', defaultMachine: 'press-hombros', equipment: 'mancuernas', cues: ['Banco a 30 grados', 'Rango completo', 'Sin chocar las mancuernas'] },
    { id: 'jalon-al-pecho', name: 'Jalon al pecho', muscleGroup: 'espalda', defaultMachine: 'jalon-pecho', equipment: 'polea', cues: ['Pecho arriba', 'Llevar la barra al pecho', 'Sin balanceo', 'Apreta la espalda'] },
    { id: 'remo-sentado-polea', name: 'Remo sentado en polea', muscleGroup: 'espalda', defaultMachine: 'remo-sentado', equipment: 'polea', cues: ['Espalda recta', 'Codos pegados al cuerpo', 'Apreta omoplatos', 'Sin impulso lumbar'] },
    { id: 'dominadas', name: 'Dominadas asistidas', muscleGroup: 'espalda', defaultMachine: 'dominadas-asistidas', equipment: 'peso-corporal', cues: ['Barbilla sobre la barra', 'Bajar controlado', 'Rango completo'] },
    { id: 'remo-mancuerna', name: 'Remo con mancuerna', muscleGroup: 'espalda', equipment: 'mancuernas', cues: ['Apoyo en banco', 'Espalda neutra', 'Codo hacia atras'] },
    { id: 'prensa', name: 'Prensa de piernas', muscleGroup: 'piernas', defaultMachine: 'prensa-piernas', equipment: 'maquina-placas', cues: ['Espalda baja apoyada', 'Rodillas alineadas con los pies', 'Sin bloquear rodillas', 'Rango completo'] },
    { id: 'sentadilla-hack-ej', name: 'Sentadilla hack', muscleGroup: 'piernas', defaultMachine: 'sentadilla-hack', equipment: 'maquina-placas', cues: ['Talones apoyados', 'Bajar a 90 grados', 'Sin colapsar rodillas'] },
    { id: 'extension-cuadriceps', name: 'Extension de cuadriceps', muscleGroup: 'piernas', defaultMachine: 'ext-cuadriceps', equipment: 'maquina-guiada', cues: ['Espalda apoyada', 'Extension completa', 'Sin impulso'] },
    { id: 'curl-femoral-ej', name: 'Curl de femoral', muscleGroup: 'piernas', defaultMachine: 'curl-femoral', equipment: 'maquina-guiada', cues: ['Cadera apoyada', 'Movimiento controlado', 'Rango completo'] },
    { id: 'sentadilla-barra', name: 'Sentadilla con barra', muscleGroup: 'piernas', equipment: 'barra', cues: ['Pecho arriba', 'Rodillas hacia afuera', 'Profundidad por debajo del paralelo'] },
    { id: 'press-hombros-ej', name: 'Press de hombros', muscleGroup: 'hombros', defaultMachine: 'press-hombros', equipment: 'mancuernas', cues: ['Core firme', 'Sin arquear la lumbar', 'Rango completo'] },
    { id: 'elevacion-lateral-ej', name: 'Elevacion lateral', muscleGroup: 'hombros', defaultMachine: 'elevacion-lateral', equipment: 'mancuernas', cues: ['Codos levemente flexionados', 'Subir a la altura del hombro', 'Sin impulso'] },
    { id: 'press-militar-barra', name: 'Press militar con barra', muscleGroup: 'hombros', equipment: 'barra', cues: ['Gluteos apretados', 'Barra sobre la cabeza', 'Sin arquear la espalda'] },
    { id: 'curl-biceps-ej', name: 'Curl de biceps', muscleGroup: 'brazos', defaultMachine: 'curl-biceps', equipment: 'mancuernas', cues: ['Codos fijos', 'Sin balanceo', 'Apreta arriba'] },
    { id: 'extension-triceps-ej', name: 'Extension de triceps en polea', muscleGroup: 'brazos', defaultMachine: 'ext-triceps', equipment: 'polea', cues: ['Codos pegados al cuerpo', 'Extension completa', 'Sin mover el hombro'] },
    { id: 'curl-martillo', name: 'Curl martillo', muscleGroup: 'brazos', equipment: 'mancuernas', cues: ['Agarre neutro', 'Codos fijos', 'Movimiento controlado'] },
    { id: 'abdominales-maquina-ej', name: 'Abdominales en maquina', muscleGroup: 'core', defaultMachine: 'abdominal-maquina', equipment: 'maquina-guiada', cues: ['Apreta el abdomen', 'Sin tirar del cuello', 'Movimiento controlado'] },
    { id: 'plancha-ej', name: 'Plancha', muscleGroup: 'core', defaultMachine: 'plancha', equipment: 'peso-corporal', cues: ['Cadera neutra', 'Gluteos apretados', 'Sin hundir la lumbar'] }
  ],
  setSchemes: [
    { id: '3x8', name: '3x8' },
    { id: '3x10', name: '3x10' },
    { id: '3x12', name: '3x12' },
    { id: '4x6', name: '4x6' },
    { id: '4x8', name: '4x8' },
    { id: '4x10', name: '4x10' },
    { id: '5x5', name: '5x5' },
    { id: 'piramidal', name: 'Piramidal' },
    { id: 'drop-set', name: 'Drop set' },
    { id: 'al-fallo', name: 'Al fallo' },
    { id: 'amrap', name: 'AMRAP' }
  ],
  repRanges: [
    { id: '1-5', name: '1-5 (fuerza)' },
    { id: '6-8', name: '6-8' },
    { id: '8-12', name: '8-12 (hipertrofia)' },
    { id: '12-15', name: '12-15' },
    { id: '15-20', name: '15-20' },
    { id: '20+', name: '20+ (resistencia)' }
  ],
  weightUnits: [
    { id: 'placa', name: 'Placa (stack)' },
    { id: 'kg', name: 'kg' },
    { id: 'lb', name: 'lb' },
    { id: 'nivel', name: 'Nivel de asistencia' },
    { id: 'peso-corporal', name: 'Peso corporal' }
  ],
  rpeScale: [
    { value: 1, label: 'RPE 1' },
    { value: 2, label: 'RPE 2' },
    { value: 3, label: 'RPE 3' },
    { value: 4, label: 'RPE 4' },
    { value: 5, label: 'RPE 5' },
    { value: 6, label: 'RPE 6' },
    { value: 6.5, label: 'RPE 6.5' },
    { value: 7, label: 'RPE 7' },
    { value: 7.5, label: 'RPE 7.5' },
    { value: 8, label: 'RPE 8' },
    { value: 8.5, label: 'RPE 8.5' },
    { value: 9, label: 'RPE 9' },
    { value: 9.5, label: 'RPE 9.5' },
    { value: 10, label: 'RPE 10' }
  ],
  restOptions: [
    { seconds: 0, label: '0:00' },
    { seconds: 30, label: '0:30' },
    { seconds: 45, label: '0:45' },
    { seconds: 60, label: '1:00' },
    { seconds: 90, label: '1:30' },
    { seconds: 120, label: '2:00' },
    { seconds: 180, label: '3:00' },
    { seconds: 300, label: '5:00' }
  ],
  setNumbers: [
    { value: 0, label: 'Calentamiento' },
    { value: 1, label: 'Serie 1' },
    { value: 2, label: 'Serie 2' },
    { value: 3, label: 'Serie 3' },
    { value: 4, label: 'Serie 4' },
    { value: 5, label: 'Serie 5' },
    { value: 6, label: 'Serie 6' },
    { value: 7, label: 'Serie 7' },
    { value: 8, label: 'Serie 8' },
    { value: 9, label: 'Serie 9' },
    { value: 10, label: 'Serie 10' }
  ],
  workoutTemplates: [
    { id: 'empuje', name: 'Empuje (Push)', exerciseIds: ['press-banca-barra', 'press-hombros-ej', 'elevacion-lateral-ej', 'extension-triceps-ej', 'aperturas-peck'] },
    { id: 'tiron', name: 'Tiron (Pull)', exerciseIds: ['jalon-al-pecho', 'remo-sentado-polea', 'dominadas', 'curl-biceps-ej', 'curl-martillo'] },
    { id: 'pierna', name: 'Pierna', exerciseIds: ['sentadilla-barra', 'prensa', 'extension-cuadriceps', 'curl-femoral-ej', 'sentadilla-hack-ej'] },
    { id: 'full-body', name: 'Full-body', exerciseIds: ['sentadilla-barra', 'press-banca-barra', 'jalon-al-pecho', 'press-hombros-ej', 'plancha-ej'] }
  ]
};

@Injectable()
export class GymDataService {

  private data: GymData = SEED;
  private loaded = false;

  constructor(private http: HttpClient) {
    this.load();
  }

  /**
   * Refresh the in-memory data from assets/gym-data.json. Best-effort: if the
   * file cannot be read (e.g. native shell), the SEED stays in place so every
   * dropdown still has data.
   */
  public load(): Promise<GymData> {
    return new Promise((resolve) => {
      this.http.get('assets/gym-data.json').subscribe(
        (json: any) => {
          if (json) {
            this.data = json as GymData;
          }
          this.loaded = true;
          resolve(this.data);
        },
        () => {
          this.loaded = true;
          resolve(this.data);
        }
      );
    });
  }

  public all(): GymData {
    return this.data;
  }

  public isLoaded(): boolean {
    return this.loaded;
  }

  public muscleGroups(): MuscleGroup[] {
    return this.data.muscleGroups;
  }

  public equipment(): EquipmentType[] {
    return this.data.equipment;
  }

  public equipmentBy(id: string): EquipmentType {
    let list = this.data.equipment;
    for (let i = 0; i < list.length; i++) {
      if (list[i].id === id) { return list[i]; }
    }
    return null;
  }

  public machines(): Machine[] {
    return this.data.machines;
  }

  public machineBy(idOrQr: string): Machine {
    let list = this.data.machines;
    for (let i = 0; i < list.length; i++) {
      if (list[i].id === idOrQr || list[i].qr === idOrQr) { return list[i]; }
    }
    return null;
  }

  public machinesFor(groupId: string): Machine[] {
    let out: Machine[] = [];
    let list = this.data.machines;
    for (let i = 0; i < list.length; i++) {
      if (list[i].muscleGroup === groupId) { out.push(list[i]); }
    }
    return out;
  }

  public exercises(): Exercise[] {
    return this.data.exercises;
  }

  public exercisesFor(groupId: string): Exercise[] {
    if (!groupId) { return this.data.exercises; }
    let out: Exercise[] = [];
    let list = this.data.exercises;
    for (let i = 0; i < list.length; i++) {
      if (list[i].muscleGroup === groupId) { out.push(list[i]); }
    }
    return out;
  }

  public exerciseBy(id: string): Exercise {
    let list = this.data.exercises;
    for (let i = 0; i < list.length; i++) {
      if (list[i].id === id) { return list[i]; }
    }
    return null;
  }

  public setSchemes(): SetScheme[] {
    return this.data.setSchemes;
  }

  public repRanges(): RepRange[] {
    return this.data.repRanges;
  }

  public units(): WeightUnit[] {
    return this.data.weightUnits;
  }

  public rpe(): RpeOption[] {
    return this.data.rpeScale;
  }

  public restOptions(): RestOption[] {
    return this.data.restOptions;
  }

  public setNumbers(): SetNumber[] {
    return this.data.setNumbers;
  }

  public templates(): WorkoutTemplate[] {
    return this.data.workoutTemplates;
  }

  /** Convenience name arrays for <item-select-other> [options] (string[]). */
  public muscleGroupNames(): string[] {
    return this.names(this.data.muscleGroups);
  }

  public equipmentNames(): string[] {
    return this.names(this.data.equipment);
  }

  public exerciseNames(): string[] {
    return this.names(this.data.exercises);
  }

  public setSchemeNames(): string[] {
    return this.names(this.data.setSchemes);
  }

  public repRangeNames(): string[] {
    return this.names(this.data.repRanges);
  }

  public unitNames(): string[] {
    return this.names(this.data.weightUnits);
  }

  public templateNames(): string[] {
    return this.names(this.data.workoutTemplates);
  }

  private names(list: Array<{ name: string }>): string[] {
    let out: string[] = [];
    for (let i = 0; i < list.length; i++) {
      out.push(list[i].name);
    }
    return out;
  }

}



// WEBPACK FOOTER //
// ./src/services/gym-data.service.ts